import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TURNSTILE_EMAIL_CODE_ACTION } from 'fym-shared/src/music24/turnstile';
import { loadTurnstileScript } from './loadTurnstileScript';

/**
 * How long a send waits for the token. The invisible check takes about a second; an interactive
 * one takes longer. After the wait the request goes without a token and music24-api decides: with
 * Turnstile on it refuses, and the page asks the person to try again.
 */
export const TURNSTILE_TOKEN_WAIT_MS = 30_000;

/**
 * - `off`: no site key in this build. No script loads and a send goes without a token.
 * - `checking`: the check runs (no token yet).
 * - `ready`: a token waits for the next send.
 * - `failed`: the script or the check failed. A send goes without a token.
 */
export type TurnstileStatus = 'off' | 'checking' | 'ready' | 'failed';

export interface UseTurnstileResult {
  /** Put it on the element that holds the widget (`TurnstileSlot`). */
  slotRef: (element: HTMLElement | null) => void;
  /** True once Cloudflare shows an interactive check. The slot is collapsed until then. */
  isVisible: boolean;
  /**
   * True while the widget shows an interactive check and has no token: the person must tick the
   * box. The page keeps the send buttons off and starts no 30 s wait (onboarding review 2026-10-19,
   * finding 2). An invisible check never sets it: there the server decides.
   */
  needsInteraction: boolean;
  status: TurnstileStatus;
  /**
   * The token for one send. A token is single use, so the widget then starts a new check for the
   * next send (the resend). Resolves `undefined` when there is no token in time.
   */
  takeToken: () => Promise<string | undefined>;
  /** Runs the check again, for example after music24-api refused the token. */
  reset: () => void;
}

type Waiter = (token: string | undefined) => void;

/**
 * Cloudflare Turnstile for the email-code sign-in, in the hidden mode: the widget renders with
 * `appearance: 'interaction-only'`, so the person sees nothing unless Cloudflare asks for an
 * interactive check. The same pattern as the Fragplace sign-in (`TurnstileWidget.tsx` there).
 *
 * The script loads only when `siteKey` is set and a slot element is on the page.
 */
export function useTurnstile(params: {
  siteKey: string;
  theme: 'light' | 'dark';
  /** Called each time the widget reports a new token, for example to clear a refused-check error. */
  onToken?: () => void;
}): UseTurnstileResult {
  const { siteKey, theme } = params;
  // Read at callback time, so a new function from the caller needs no new widget.
  const onTokenRef = useRef(params.onToken);
  onTokenRef.current = params.onToken;
  const enabled = siteKey !== '';
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [status, setStatus] = useState<TurnstileStatus>(enabled ? 'checking' : 'off');
  const tokenRef = useRef<string | null>(null);
  const failedRef = useRef(false);
  const widgetRef = useRef<{ api: TurnstileApi; id: string } | null>(null);
  const waitersRef = useRef<Waiter[]>([]);

  const settleWaiters = useCallback((token: string | undefined) => {
    const waiters = waitersRef.current;
    waitersRef.current = [];
    waiters.forEach((resolve) => resolve(token));
  }, []);

  const restartCheck = useCallback(() => {
    tokenRef.current = null;
    failedRef.current = false;
    // A new check starts hidden; Cloudflare shows the box again only when it asks for a tick.
    setIsVisible(false);
    const widget = widgetRef.current;
    if (widget !== null) {
      setStatus('checking');
      widget.api.reset(widget.id);
    }
  }, []);

  const slotRef = useCallback((element: HTMLElement | null) => setSlot(element), []);

  useEffect(() => {
    if (!enabled || slot === null) {
      return undefined;
    }
    let cancelled = false;
    setStatus('checking');
    setIsVisible(false);
    loadTurnstileScript()
      .then((api) => {
        if (cancelled) {
          return;
        }
        const id = api.render(slot, {
          sitekey: siteKey,
          action: TURNSTILE_EMAIL_CODE_ACTION,
          appearance: 'interaction-only',
          size: 'flexible',
          theme,
          'refresh-expired': 'auto',
          callback: (token) => {
            tokenRef.current = token;
            failedRef.current = false;
            setStatus('ready');
            onTokenRef.current?.();
            if (waitersRef.current.length > 0) {
              tokenRef.current = null;
              settleWaiters(token);
              restartCheck();
            }
          },
          'error-callback': () => {
            tokenRef.current = null;
            failedRef.current = true;
            setStatus('failed');
            settleWaiters(undefined);
          },
          'expired-callback': () => {
            tokenRef.current = null;
            setIsVisible(false);
            setStatus('checking');
          },
          'before-interactive-callback': () => setIsVisible(true),
        });
        if (id !== undefined) {
          widgetRef.current = { api, id };
        }
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        failedRef.current = true;
        setStatus('failed');
        settleWaiters(undefined);
      });
    return () => {
      cancelled = true;
      const widget = widgetRef.current;
      widgetRef.current = null;
      tokenRef.current = null;
      if (widget !== null) {
        widget.api.remove(widget.id);
      }
    };
  }, [enabled, slot, siteKey, theme, settleWaiters, restartCheck]);

  // A page that unmounts while a send waits lets the send go on at once.
  useEffect(() => () => settleWaiters(undefined), [settleWaiters]);

  const takeToken = useCallback((): Promise<string | undefined> => {
    if (!enabled) {
      return Promise.resolve(undefined);
    }
    const token = tokenRef.current;
    if (token !== null) {
      tokenRef.current = null;
      restartCheck();
      return Promise.resolve(token);
    }
    if (failedRef.current) {
      // The check failed in the browser. The request goes without a token; music24-api decides.
      restartCheck();
      return Promise.resolve(undefined);
    }
    return new Promise<string | undefined>((resolve) => {
      let done = false;
      let timer: ReturnType<typeof setTimeout> | undefined;
      const finish: Waiter = (value) => {
        if (!done) {
          done = true;
          clearTimeout(timer);
          resolve(value);
        }
      };
      timer = setTimeout(() => {
        waitersRef.current = waitersRef.current.filter((waiter) => waiter !== finish);
        finish(undefined);
      }, TURNSTILE_TOKEN_WAIT_MS);
      waitersRef.current.push(finish);
    });
  }, [enabled, restartCheck]);

  const needsInteraction = enabled && isVisible && status === 'checking';

  return useMemo(
    () => ({ slotRef, isVisible, needsInteraction, status, takeToken, reset: restartCheck }),
    [slotRef, isVisible, needsInteraction, status, takeToken, restartCheck]
  );
}
